import { type ReactNode } from "react";
import { cn } from "@/lib/utils";

/**
 * Scroll-triggered motion is CSS-only (view timelines). No IntersectionObserver
 * and no layout reads after hydration — those showed up as Lighthouse forced reflow.
 */
export function Reveal({
  children,
  delay = 0,
  className,
  as = "div",
}: {
  children: ReactNode;
  delay?: number;
  y?: number;
  className?: string;
  as?: "div" | "span" | "li";
}) {
  const Tag = as;

  return (
    <Tag
      className={cn("reveal-in", className)}
      style={delay ? { animationDelay: `${delay}s` } : undefined}
    >
      {children}
    </Tag>
  );
}

export function MaskedHeadline({
  lines,
  className,
  lineClassName,
  delay = 0,
}: {
  lines: string[];
  className?: string;
  lineClassName?: string;
  delay?: number;
}) {
  return (
    <span className={cn("block", className)}>
      {lines.map((line, i) => (
        <span key={line} className="block overflow-hidden pt-[0.04em] pb-[0.2em] -mb-[0.16em]">
          <span
            className={cn("mask-line block", lineClassName)}
            style={{ animationDelay: `${delay + i * 0.09}s` }}
          >
            {line}
          </span>
        </span>
      ))}
    </span>
  );
}

export function EngravedLine({ className }: { className?: string }) {
  return <div className={cn("draw-line h-px w-full bg-brass/50", className)} />;
}

export function Parallax({
  children,
  className,
}: {
  children: ReactNode;
  amount?: number;
  className?: string;
}) {
  return <div className={cn("overflow-hidden", className)}>{children}</div>;
}

export function Magnetic({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
  strength?: number;
}) {
  return <span className={className}>{children}</span>;
}
